import React, { useState, useEffect, createContext, useContext } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import Section from './components/Section';
import ModuleReports from './components/ModuleReports';
import TestRunDetail from './components/TestRunDetail';
import TestHistory from './components/TestHistory';
import TestRunHistoryDetail from './components/TestRunHistoryDetail';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Legend } from 'recharts';
// import { FaInfoCircle, FaSyncAlt} from 'react-icons/fa';
import SettingsPage from './components/SettingsProjects';
import { Box, Flex, Stack, Heading, Text, Button, SimpleGrid, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@chakra-ui/react';


export const ThemeContext = createContext();
export function useTheme() {
  return useContext(ThemeContext);
}

const baseUrl = process.env.REACT_APP_API_BASE_URL;

// function MetricCard({ label, value, trend, chart }) {
//   return (
//     <div className="metric-card-v2">
//       <div className="metric-card-header">
//         <span className="metric-card-label">{label} <FaInfoCircle className="metric-info-icon" /></span>
//         <ResponsiveContainer width="40%" height={36}>
//           <LineChart data={chart.map((y, i) => ({ y, x: i }))} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
//             <Line type="monotone" dataKey="y" stroke="var(--color-primary-blue)" strokeWidth={2} dot={false} />
//           </LineChart>
//         </ResponsiveContainer>
//       </div>
//       <div className="metric-card-value-row">
//         <span className="metric-card-value">{value}</span>
//       </div>
//       <div className="metric-card-trend-row">
//         <span className="metric-card-trend"><FaSyncAlt className="metric-trend-icon" /> {trend} <span className="metric-card-trend-label">last run</span></span>
//       </div>
//     </div>
//   );
// }

function Dashboard({ loading, selectedProject, runs }) {
  const [displayPeriod, setDisplayPeriod] = useState('weekly');

  // // Chakra color mode values for the Dashboard page
  // const textColor = useColorModeValue('gray.800', 'gray.100');
  // const cardBg = useColorModeValue('white', 'gray.800');
  // const cardBorder = useColorModeValue('gray.200', 'gray.700');
  // const selectBg = useColorModeValue('gray.50', 'gray.700');
  // const selectBorder = useColorModeValue('gray.200', 'gray.600');
  // const selectColor = useColorModeValue('gray.800', 'gray.100');

  if (loading) {
    return <Flex direction="column" align="center" justify="center" minH="60vh"><Heading size="md" color="gray.500">Loading...</Heading></Flex>;
  }

  // AGGREGATE DATA ACROSS ALL PROJECTS IF NO PROJECT SELECTED
  let filteredRuns = runs || [];
  let dashboardLabel = selectedProject && selectedProject !== 'ALL PROJECTS' ? selectedProject : 'All Projects';
  if (selectedProject && selectedProject !== 'ALL PROJECTS') {
    filteredRuns = filteredRuns.filter(r => r.project === selectedProject);
  }
  if (!filteredRuns.length) {
    return <div className="metrics-main"><div className="metrics-topbar"><span className="metrics-title" style={{color: 'var(--color-text-dark)'}}>No or invalid data for {dashboardLabel}.</span></div></div>;
  }

  // Use the most recent run for metrics cards
  const lastRun = filteredRuns[0]?.summary || {};
  const total = lastRun.tests ?? 0;
  const failed = lastRun.failures ?? 0;
  const skipped = lastRun.skipped ?? 0;
  const errors = lastRun.errors ?? 0;
  const passed = total - failed - skipped;
  const time = lastRun.time ?? 0;

  // Prepare trend data for chart based on displayPeriod
  const getPeriodRuns = (allRuns, period) => {
    const now = new Date();
    let startDate = new Date();
    switch (period) {
      case 'monthly': startDate.setDate(now.getDate() - 30); break;
      case 'quarterly': startDate.setDate(now.getDate() - 90); break;
      case 'weekly':
      default: startDate.setDate(now.getDate() - 7); break;
    }
    return allRuns.filter(run => run.timestamp && new Date(run.timestamp) >= startDate);
  };
  const runsForPeriod = getPeriodRuns(filteredRuns, displayPeriod);
  const sourceForTrendData = runsForPeriod.length > 0 ? runsForPeriod : filteredRuns;
  const trendData = sourceForTrendData.map(run => {
    const summary = run.summary || {};
    const t = summary.tests ?? 0;
    const f = summary.failures ?? 0;
    const s = summary.skipped ?? 0;
    const e = summary.errors ?? 0;
    const p = t - f - s;
    return {
      date: run.timestamp ? new Date(run.timestamp).toISOString().slice(0, 10) : '',
      passPct: t ? ((p / t) * 100) : 0,
      buildStability: t ? (((t - f - e) / t) * 100) : 0,
      flakiness: t ? ((s / t) * 100) : 0,
      tests: t,
      failed: f,
      flaky: s,
      testRuns: 1,
      buildDuration: summary.time ?? 0,
      passed: p,
      errors: e,
    };
  }).reverse();

  // AGGREGATE METRICS FOR ALL RUNS IF ALL PROJECTS
  let agg = { total: 0, failed: 0, skipped: 0, errors: 0, passed: 0, time: 0, runs: 0 };
  if (!selectedProject || selectedProject === 'ALL PROJECTS') {
    filteredRuns.forEach(run => {
      const s = run.summary || {};
      agg.total += s.tests ?? 0;
      agg.failed += s.failures ?? 0;
      agg.skipped += s.skipped ?? 0;
      agg.errors += s.errors ?? 0;
      agg.time += s.time ?? 0;
      agg.runs += 1;
    });
    agg.passed = agg.total - agg.failed - agg.skipped;
  }

  const metrics = {
    health: [
      { label: 'PASS PERCENTAGE', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? (total ? ((passed / total) * 100).toFixed(1) + '%' : '-') : (agg.total ? ((agg.passed / agg.total) * 100).toFixed(1) + '%' : '-'), trend: '-', chart: trendData.map(d => ({ y: d.passPct, date: d.date })) },
      { label: 'BUILD STABILITY', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? (total ? (((total - failed - errors) / total) * 100).toFixed(1) + '%' : '-') : (agg.total ? (((agg.total - agg.failed - agg.errors) / agg.total) * 100).toFixed(1) + '%' : '-'), trend: '-', chart: trendData.map(d => ({ y: d.buildStability, date: d.date })) },
      { label: 'FLAKINESS', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? (total ? ((skipped / total) * 100).toFixed(1) + '%' : '-') : (agg.total ? ((agg.skipped / agg.total) * 100).toFixed(1) + '%' : '-'), trend: '-', chart: trendData.map(d => ({ y: d.flakiness, date: d.date })) },
    ],
    tests: [
      { label: 'TESTS', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? total : agg.total, trend: '-', chart: trendData.map(d => ({ y: d.tests, date: d.date })) },
      { label: 'FAILED', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? failed : agg.failed, trend: '-', chart: trendData.map(d => ({ y: d.failed, date: d.date })) },
      { label: 'FLAKY', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? skipped : agg.skipped, trend: '-', chart: trendData.map(d => ({ y: d.flaky, date: d.date })) },
    ],
    execution: [
      { label: 'TEST RUNS', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? filteredRuns.length : agg.runs, trend: '-', chart: trendData.map(d => ({ y: d.testRuns, date: d.date })) },
      { label: 'BUILD DURATION', value: (selectedProject && selectedProject !== 'ALL PROJECTS') ? time.toFixed(1) + 's' : agg.time.toFixed(1) + 's', trend: '-', chart: trendData.map(d => ({ y: d.buildDuration, date: d.date })) },
    ],
  };

  // --- NEW CHARTS DATA ---
  // Pie chart for test status distribution (pass/fail/skipped/errors)
  const lastOrAgg = (selectedProject && selectedProject !== 'ALL PROJECTS') ? lastRun : agg;
  const pieData = [
    { name: 'Passed', value: lastOrAgg.passed, color: '#16a34a' },
    { name: 'Failed', value: lastOrAgg.failed, color: '#ef4444' },
    { name: 'Skipped', value: lastOrAgg.skipped, color: '#fbbf24' },
    { name: 'Errors', value: lastOrAgg.errors, color: '#6366f1' },
  ];

  // Line chart for average test duration over time
  const durationLineData = trendData.map(d => ({ date: d.date, duration: d.buildDuration }));

  // Stacked bar chart for runs by project (if all projects)
  let runsByProject = [];
  if (!selectedProject || selectedProject === 'ALL PROJECTS') {
    const projectMap = {};
    runs.forEach(run => {
      const proj = run.project || 'Unknown';
      if (!projectMap[proj]) projectMap[proj] = 0;
      projectMap[proj] += 1;
    });
    runsByProject = Object.entries(projectMap).map(([name, value]) => ({ name, value }));
  }

  return (
    <Box w="100%" mt={8} px={{ base: 4, md: 8 }}>
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Heading size="lg">Metrics <Text as="span" color="gray.400" fontWeight="normal" fontSize="md">- {dashboardLabel}</Text></Heading>
        <Flex gap={2}>
          <Button size="sm" variant={displayPeriod === 'weekly' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setDisplayPeriod('weekly')} borderRadius="lg">Weekly</Button>
          <Button size="sm" variant={displayPeriod === 'monthly' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setDisplayPeriod('monthly')} borderRadius="lg">Monthly</Button>
          <Button size="sm" variant={displayPeriod === 'quarterly' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setDisplayPeriod('quarterly')} borderRadius="lg">Quarterly</Button>
        </Flex>
      </Flex>
      <Stack spacing={8} mb={8}>
        <Section title="Health Metrics" metrics={metrics.health} />
        <Section title="Tests Metrics" metrics={metrics.tests} />
        <Section title="Execution Metrics" metrics={metrics.execution} />
      </Stack>
      <SimpleGrid columns={{ base: 1, md: 2, lg: runsByProject.length > 0 ? 3 : 2 }} spacing={8} mb={8}>
        <Box bg="white" _dark={{ bg: 'gray.800' }} borderRadius="lg" p={6} boxShadow="md" borderWidth={1}>
          <Text fontWeight="bold" fontSize="lg" mb={3}>Test Status Distribution</Text>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label>
                {pieData.map((entry, idx) => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <ReTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box bg="white" _dark={{ bg: 'gray.800' }} borderRadius="lg" p={6} boxShadow="md" borderWidth={1}>
          <Text fontWeight="bold" fontSize="lg" mb={3}>Average Build Duration Over Time</Text>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={durationLineData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" stroke="#888" fontSize="0.875rem" />
              <YAxis stroke="#888" fontSize="0.875rem" />
              <ReTooltip />
              <Legend />
              <Line type="monotone" dataKey="duration" name="Duration (s)" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        {runsByProject.length > 0 && (
          <Box bg="white" _dark={{ bg: 'gray.800' }} borderRadius="lg" p={6} boxShadow="md" borderWidth={1}>
            <Text fontWeight="bold" fontSize="lg" mb={3}>Runs by Project</Text>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={runsByProject} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#888" fontSize="0.875rem" />
                <YAxis stroke="#888" fontSize="0.875rem" />
                <ReTooltip />
                <Legend />
                <Bar dataKey="value" name="Runs" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </SimpleGrid>
    </Box>
  );
}

function TestRunDetailPage({ junitData }) {
  const { moduleName } = useParams();
  const decodedModule = decodeURIComponent(moduleName);
  const testcases = junitData?.testcases || [];
  const cases = testcases.filter(tc => (tc.classname || 'Unknown') === decodedModule);
  return <TestRunDetail moduleName={decodedModule} cases={cases} />;
}

function App() {
  // Load projects from localStorage or fallback to default
  const defaultProjects = [
    { name: 'ALL PROJECTS' },
    { name: 'Pest Tests', env: 'pest-tests' },
    { name: 'API Tests', env: 'api-tests' },
  ];
  const [projects, setProjects] = useState(() => {
    const stored = localStorage.getItem('projects');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return defaultProjects;
      }
    }
    return defaultProjects;
  });
  // selectedProject is a string (project name)
  const [selectedProject, setSelectedProject] = useState(() => {
    const stored = localStorage.getItem('selectedProject');
    if (stored) return stored;
    return projects[0]?.name || 'ALL PROJECTS';
  });
  useEffect(() => {
    localStorage.setItem('selectedProject', selectedProject);
  }, [selectedProject]);
  const [junitData, setJunitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('theme-dark');
    if (stored !== null) return stored === 'true';
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (dark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    localStorage.setItem('theme-dark', dark);
  }, [dark]);
  const [allRuns, setAllRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);

  useEffect(() => {
    setRunsLoading(true);
    async function fetchRuns() {
      let runsArr = [];
      if (selectedProject === 'ALL PROJECTS') {
        // Fetch all runs across all projects from the new endpoint
        runsArr = await fetch(`${baseUrl}/api/test-run-history`)
          .then(r => r.json())
          .catch(() => []);
      } else {
        // Use selectedProject (project name) directly
        runsArr = await fetch(`${baseUrl}/api/test-runs?project=${encodeURIComponent(selectedProject)}`)
          .then(r => r.json())
          .catch(() => []);
      }
      setAllRuns(runsArr);
      setRunsLoading(false);
    }
    fetchRuns();
  }, [selectedProject, projects]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await fetch(`${baseUrl}/api/test-runs?project=api-tests`);
        const data = await res.json();
        setJunitData(data);
      } catch (e) {
        setJunitData(null);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <ThemeContext.Provider value={{ dark, setDark, projects, setProjects }}>
      <Router>
        <div className="app-layout">
          <Sidebar selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects} />
          <Box flex="1" overflowX="auto">
            <Routes>
              <Route path="/" element={<Dashboard junitData={junitData} loading={loading || runsLoading} selectedProject={selectedProject} projects={projects} runs={allRuns} setSelectedProject={setSelectedProject} />} />
              <Route path="/modules" element={<ModuleReports junitData={junitData} loading={loading || runsLoading} selectedProject={selectedProject} projects={projects} runs={allRuns} />} />
              <Route path="/modules/:moduleName" element={<TestRunDetailPage junitData={junitData} />} />
              <Route path="/history" element={<HistoryPage runs={allRuns} loading={loading || runsLoading} selectedProject={selectedProject} projects={projects} />} />
              <Route path="/history/:runId" element={<TestRunHistoryDetail selectedProject={selectedProject} runs={allRuns} loading={loading || runsLoading} />} />
              <Route path="/compare/p1/:project1/r1/:id1/p2/:project2/r2/:id2" element={<CompareRunsPage />} />
              <Route path="/settings" element={<SettingsPage projects={projects} setProjects={setProjects} selectedProject={selectedProject} setSelectedProject={setSelectedProject} />} />
            </Routes>
          </Box>
        </div>
      </Router>
    </ThemeContext.Provider>
  );
}

function HistoryPage({ runs, loading, selectedProject, projects }) {
  const navigate = useNavigate();
  return <TestHistory 
    runs={runs} 
    loading={loading} 
    onSelectRun={id => navigate(`/history/${id}`)} 
    onCompareRuns={(run1, run2) => 
      navigate(`/compare/p1/${encodeURIComponent(run1.project)}/r1/${run1.id}/p2/${encodeURIComponent(run2.project)}/r2/${run2.id}`)
    } 
    selectedProject={selectedProject} 
    projects={projects} 
  />;
}

function CompareRunsPage() {
  const { project1, id1, project2, id2 } = useParams();
  const [run1, setRun1] = useState(null);
  const [run2, setRun2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [selectedTestcasePair, setSelectedTestcasePair] = useState(null); // {tc1, tc2, status}

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${baseUrl}/api/test-runs/${id1}?project=${encodeURIComponent(project1)}`).then(r => r.json()),
      fetch(`${baseUrl}/api/test-runs/${id2}?project=${encodeURIComponent(project2)}`).then(r => r.json())
    ]).then(([r1, r2]) => {
      setRun1(r1);
      setRun2(r2);
      setError(null);
    }).catch(e => setError('Failed to load test runs'))
      .finally(() => setLoading(false));
  }, [project1, id1, project2, id2]);

  if (loading) return <div style={{ padding: 32 }}>Loading runs...</div>;
  if (error) return <div style={{ color: 'red', padding: 32 }}>{error}</div>;
  if (!run1 || !run2) return <div style={{ padding: 32 }}>One or both runs not found.</div>;

  // Safely access summaries
  const run1Summary = run1.summary || {};
  const run2Summary = run2.summary || {};

  function normalize(str) {
    return (str || '').trim().toLowerCase();
  }
  function key(tc) {
    return `${normalize(tc.classname)}::${normalize(tc.name)}`;
  }

  const map1 = Object.fromEntries((run1.testcases || []).map(tc => [key(tc), tc]));
  const map2 = Object.fromEntries((run2.testcases || []).map(tc => [key(tc), tc]));
  const allKeys = Array.from(new Set([...Object.keys(map1), ...Object.keys(map2)]));

  const diffRows = allKeys.map(k => {
    const tc1 = map1[k];
    const tc2 = map2[k];
    let status = '';
    if (tc1 && !tc2) status = 'Removed';
    else if (!tc1 && tc2) status = 'Added';
    else if (tc1 && tc2) {
      const fail1 = tc1.failure || tc1.error;
      const fail2 = tc2.failure || tc2.error;
      if (fail1 && !fail2) status = 'Fixed';
      else if (!fail1 && fail2) status = 'New Failure';
      else if (fail1 && fail2) status = 'Still Failing';
      else status = 'Unchanged';
    }
    return { key: k, tc1, tc2, status };
  });

  const statusOptions = ['All', 'Added', 'Removed', 'Fixed', 'New Failure', 'Still Failing', 'Unchanged'];
  const filteredRows = filter === 'All' ? diffRows : diffRows.filter(r => r.status === filter);

  const summary = {
    added: diffRows.filter(r => r.status === 'Added').length,
    removed: diffRows.filter(r => r.status === 'Removed').length,
    fixed: diffRows.filter(r => r.status === 'Fixed').length,
    newFailures: diffRows.filter(r => r.status === 'New Failure').length,
    stillFailing: diffRows.filter(r => r.status === 'Still Failing').length,
    unchanged: diffRows.filter(r => r.status === 'Unchanged').length,
  };

  function errorMsg(tc) {
    return tc?.failure?.message || tc?.error?.message || tc?.failure?._ || tc?.error?._ || tc?.failure || tc?.error || '';
  }

  // Analytics - use safe summaries and provide defaults
  const pass1 = (run1Summary.tests ?? 0) - (run1Summary.failures ?? 0) - (run1Summary.errors ?? 0);
  const pass2 = (run2Summary.tests ?? 0) - (run2Summary.failures ?? 0) - (run2Summary.errors ?? 0);
  const fail1 = (run1Summary.failures ?? 0) + (run1Summary.errors ?? 0);
  const fail2 = (run2Summary.failures ?? 0) + (run2Summary.errors ?? 0);
  const passRate1 = (run1Summary.tests ?? 0) ? (pass1 / (run1Summary.tests) * 100).toFixed(1) : '0'; // Ensure divisor isn't zero implicitly
  const passRate2 = (run2Summary.tests ?? 0) ? (pass2 / (run2Summary.tests) * 100).toFixed(1) : '0'; // Ensure divisor isn't zero implicitly
  const duration1 = run1Summary.time || 0;
  const duration2 = run2Summary.time || 0;

  function exportCSV() {
    const header = ['Module', 'Test Name', 'Status', 'Run 1', 'Run 2'];
    const rows = filteredRows.map(row => [
      row.tc1?.classname || row.tc2?.classname || 'Unknown',
      row.tc1?.name || row.tc2?.name,
      row.status,
      row.tc1 ? (row.tc1.failure || row.tc1.error ? 'Failed' : 'Passed') : '-',
      row.tc2 ? (row.tc2.failure || row.tc2.error ? 'Failed' : 'Passed') : '-',
    ]);
    const csv = [header, ...rows].map(r => r.map(x => '"' + String(x).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare-${project1}_${id1}-vs-${project2}_${id2}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  }

  // Chakra v2: Use direct color tokens for light/dark mode
  const cardBg = { base: '#fff', _dark: '#23272a' };
  const cardBorder = { base: '#dee2e6', _dark: '#33373a' };
  const sectionTitleColor = { base: '#64748b', _dark: '#b0b3b8' };
  // const tableHeaderBg = { base: '#e6f4ea', _dark: '#193c2c' };
  // const tableHeaderColor = { base: '#16a34a', _dark: '#30c55b' };
  // const tableRowBgEven = { base: '#fff', _dark: '#23272a' };
  const tableRowBgOdd = { base: '#f8f9fa', _dark: '#181a1b' };
  // const tableRowBorder = { base: '#dee2e6', _dark: '#33373a' };
  const textColor = { base: '#343a40', _dark: '#f3f4f6' };
  // const highlightRowBg = { base: 'blue.50', _dark: 'blue.900' }; // for Added/Removed/Fixed/New Failure
  // const navActiveBg = { base: 'blue.100', _dark: 'blue.800' };

  return (
    <Box w="100%" my={10} p={8} borderRadius="2xl" boxShadow="lg" bg={cardBg} px={{ base: 4, md: 8 }}>
      <Flex direction="column" mb={6}>
        <div style={{ padding: '2rem' }}>
          <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 12, color: 'var(--color-text-dark)' }}>Compare Test Runs</h2>
          <div style={{ marginBottom: 18, color: 'var(--color-text-light)' }}>
            <b>{decodeURIComponent(project1)}/{id1}</b> vs <b>{decodeURIComponent(project2)}/{id2}</b>
          </div>
          <div style={{ marginBottom: 24, display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flexGrow: 1, display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-status-info-bg)', color: 'var(--color-status-info)', fontSize: '0.875rem'}}><b>Added:</b> {summary.added}</span>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-status-info-bg)', color: 'var(--color-status-info)', fontSize: '0.875rem'}}><b>Removed:</b> {summary.removed}</span>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-status-success-bg)', color: 'var(--color-status-success)', fontSize: '0.875rem', fontWeight: 600 }}><b>Fixed:</b> {summary.fixed}</span>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-status-danger-bg)', color: 'var(--color-status-danger)', fontSize: '0.875rem', fontWeight: 600 }}><b>New Failures:</b> {summary.newFailures}</span>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-status-danger-bg)', color: 'var(--color-status-danger)', fontSize: '0.875rem', fontWeight: 600 }}><b>Still Failing:</b> {summary.stillFailing}</span>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', background: 'var(--color-border-light)', color: 'var(--color-text-light)', fontSize: '0.875rem'}}><b>Unchanged:</b> {summary.unchanged}</span>
            </div>
            <button onClick={exportCSV} className="button-base button-primary">Export as CSV</button>
          </div>
          <div className="card-base" style={{ marginBottom: 24, padding: '1rem 1.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center', background: 'var(--color-accent-bg)' }}>
            <div style={{fontSize: '0.9rem'}}>Pass Rate: <span style={{ color: passRate2 > passRate1 ? 'var(--color-status-success)' : passRate2 < passRate1 ? 'var(--color-status-danger)' : 'var(--color-text-light)', fontWeight: 600 }}>{passRate1}% → {passRate2}%</span></div>
            <div style={{fontSize: '0.9rem'}}>Failures: <span style={{ color: fail2 > fail1 ? 'var(--color-status-danger)' : fail2 < fail1 ? 'var(--color-status-success)' : 'var(--color-text-light)', fontWeight: 600 }}>{fail1} → {fail2}</span></div>
            <div style={{fontSize: '0.9rem'}}>Duration: <span style={{ color: duration2 > duration1 ? 'var(--color-status-danger)' : duration2 < duration1 ? 'var(--color-status-success)' : 'var(--color-text-light)', fontWeight: 600 }}>{duration1.toFixed(1)}s → {duration2.toFixed(1)}s</span></div>
          </div>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <label htmlFor="statusFilterCompare" style={{ fontWeight: 600, marginRight: '0.75rem', color: 'var(--color-text-dark)' }}>Filter by status:</label>
            <select id="statusFilterCompare" value={filter} onChange={e => setFilter(e.target.value)} className="select-base" style={{ width: 'auto', minWidth: '200px'}}>
              {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 'var(--card-border-radius)', boxShadow: 'var(--card-box-shadow)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--color-background-card)' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent-bg)', borderBottom: '2px solid var(--color-primary-green)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--color-primary-green)', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}>Module</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--color-primary-green)', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}>Test Name</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--color-primary-green)', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--color-primary-green)', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}>Run 1 ({project1})</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--color-primary-green)', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}>Run 2 ({project2})</th>
                  <th style={{ padding: '0.75rem 1rem', position: 'sticky', top: 0, background: 'var(--color-accent-bg)' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr key={row.key} style={{ borderBottom: '1px solid var(--color-border-light)', background: row.status === 'New Failure' ? 'var(--color-status-danger-bg)' : row.status === 'Fixed' ? 'var(--color-status-success-bg)' : (i % 2 === 0 ? 'var(--color-background-card)' : 'var(--color-background-main)') }}>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-dark)' }}>{row.tc1?.classname || row.tc2?.classname || 'Unknown'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-dark)' }}>{row.tc1?.name || row.tc2?.name}</td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600, 
                      color: row.status === 'New Failure' || row.status === 'Still Failing' ? 'var(--color-status-danger)' : 
                             row.status === 'Fixed' ? 'var(--color-status-success)' : 
                             row.status === 'Added' || row.status === 'Removed' ? 'var(--color-status-info)': 'var(--color-text-light)' 
                    }}>{row.status}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {row.tc1 ? (row.tc1.failure || row.tc1.error ? <span style={{color: 'var(--color-status-danger)', fontWeight: 'bold'}}>Failed</span> : <span style={{color: 'var(--color-status-success)', fontWeight: 'bold'}}>Passed</span>) : '-'}
                      {row.tc1?.time && <span style={{ color: 'var(--color-text-light)', fontSize: '0.8rem' }}> ({row.tc1.time}s)</span>}
                      {errorMsg(row.tc1) && <div style={{ color: 'var(--color-status-danger)', fontSize: '0.8rem', marginTop: '0.25rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{errorMsg(row.tc1)}</div>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {row.tc2 ? (row.tc2.failure || row.tc2.error ? <span style={{color: 'var(--color-status-danger)', fontWeight: 'bold'}}>Failed</span> : <span style={{color: 'var(--color-status-success)', fontWeight: 'bold'}}>Passed</span>) : '-'}
                      {row.tc2?.time && <span style={{ color: 'var(--color-text-light)', fontSize: '0.8rem' }}> ({row.tc2.time}s)</span>}
                      {errorMsg(row.tc2) && <div style={{ color: 'var(--color-status-danger)', fontSize: '0.8rem', marginTop: '0.25rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{errorMsg(row.tc2)}</div>}
                    </td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                      <Button size="sm" onClick={() => {setSelectedTestcasePair(row); onModalOpen();}} borderRadius="lg">Details</Button>
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-light)' }}>No test cases match the selected filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Flex>

      {/* Details Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent bg={cardBg} color={textColor}>
          <ModalHeader>Test Details</ModalHeader>
          <ModalBody>
            {selectedTestcasePair && (
              <Stack spacing={4}>
                <Text fontSize="md" fontWeight="bold">Status: <Text as="span" fontWeight="semibold" color={
                  selectedTestcasePair.status === 'New Failure' || selectedTestcasePair.status === 'Still Failing' ? 'red.500' :
                  selectedTestcasePair.status === 'Fixed' ? 'green.500' :
                  selectedTestcasePair.status === 'Added' || selectedTestcasePair.status === 'Removed' ? 'blue.500': textColor
                }>{selectedTestcasePair.status}</Text></Text>

                <Box>
                  <Text fontWeight="bold" mb={2}>Run 1 ({decodeURIComponent(project1)}/{id1})</Text>
                  {selectedTestcasePair.tc1 ? (
                    <Box p={3} bg={tableRowBgOdd} border="1px" borderColor={cardBorder} borderRadius="md" overflowX="auto">
                      <pre style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(selectedTestcasePair.tc1, null, 2)}</pre>
                    </Box>
                  ) : <Text color={sectionTitleColor}>Not present in this run.</Text>}
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>Run 2 ({decodeURIComponent(project2)}/{id2})</Text>
                  {selectedTestcasePair.tc2 ? (
                    <Box p={3} bg={tableRowBgOdd} border="1px" borderColor={cardBorder} borderRadius="md" overflowX="auto">
                      <pre style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(selectedTestcasePair.tc2, null, 2)}</pre>
                    </Box>
                  ) : <Text color={sectionTitleColor}>Not present in this run.</Text>}
                </Box>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={onModalClose} borderRadius="lg">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default App;
